import { HistoricalDataPoint, NetworkStats } from './types';
import { fetchCoinwarzData, convertCoinwarzToNetworkStats } from './coinwarz';
import { fetchPriceHistory } from './coingecko';
import { fetchBlockchairStats } from './blockchair';
import { fallbackHistoricalData, fallbackNetworkStats } from '../fallbackHistoricalData';
import { toast } from 'react-hot-toast';

let lastFetchTimestamp = 0;
const REFRESH_INTERVAL = 60000; // 1 minute

export async function fetchHistoricalData(coin: string, days: number = 30, forceRefresh = false): Promise<HistoricalDataPoint[]> {
  try {
    const now = Date.now();
    if (!forceRefresh && now - lastFetchTimestamp < REFRESH_INTERVAL) {
      return fallbackHistoricalData[coin as keyof typeof fallbackHistoricalData] || [];
    }

    lastFetchTimestamp = now;
    const priceData = await fetchPriceHistory(coin, days);
    if (!priceData) {
      throw new Error('Failed to fetch price history');
    }

    const coinwarzData = await fetchCoinwarzData(coin);
    const blockchairData = !coinwarzData ? await fetchBlockchairStats(coin) : null;

    const data = priceData.map(([timestamp, price]: [number, number]) => {
      let hashrate;

      if (coinwarzData) {
        hashrate = coinwarzData.Data.NetworkHashrate;
      } else if (blockchairData?.hashrate_24h) {
        hashrate = blockchairData.hashrate_24h;
      } else {
        const baseHashrate = fallbackNetworkStats[coin as keyof typeof fallbackNetworkStats].hashrate_24h;
        const daysFactor = 1 - (Math.random() * 0.2);
        hashrate = baseHashrate * daysFactor;
      }

      if (coin === 'BTC') {
        hashrate = hashrate * 1000000;
      }

      return {
        date: new Date(timestamp).toISOString(),
        price,
        networkHashrate: hashrate,
        blockReward: fallbackNetworkStats[coin as keyof typeof fallbackNetworkStats].blockReward
      };
    });

    return data;
  } catch (error) {
    console.warn('Using fallback historical data for', coin, error);
    return fallbackHistoricalData[coin as keyof typeof fallbackHistoricalData] || [];
  }
}

export async function fetchNetworkStats(coin: string, forceRefresh = false): Promise<NetworkStats> {
  try {
    const now = Date.now();
    if (!forceRefresh && now - lastFetchTimestamp < REFRESH_INTERVAL) {
      return fallbackNetworkStats[coin as keyof typeof fallbackNetworkStats];
    }

    lastFetchTimestamp = now;
    const coinwarzData = await fetchCoinwarzData(coin);
    if (coinwarzData) {
      return convertCoinwarzToNetworkStats(coinwarzData);
    }
    
    const blockchairData = await fetchBlockchairStats(coin);
    if (blockchairData) {
      return blockchairData;
    }
    
    throw new Error('No data available from APIs');
  } catch (error) {
    console.warn('Using fallback network stats for', coin, error);
    return fallbackNetworkStats[coin as keyof typeof fallbackNetworkStats];
  }
}

export async function refreshAllData(): Promise<void> {
  try {
    lastFetchTimestamp = 0; // Reset timestamp to force refresh
    const coins = ['BTC', 'ETH', 'LTC', 'KDA'];
    
    await Promise.all([
      ...coins.map(coin => fetchHistoricalData(coin, 30, true)),
      ...coins.map(coin => fetchNetworkStats(coin, true))
    ]);

    toast.success('Data refreshed successfully');
  } catch (error) {
    console.error('Error refreshing data:', error);
    toast.error('Failed to refresh data');
  }
}